import { ArrowLeftIcon } from "@phosphor-icons/react/dist/ssr";
import { Loader } from "apps/visits/packages/ui/src/components/loader";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { cn } from "@/core/shared/v2/lib/utils";
import { IntersectionFadeIn } from "@/core/shared/v2/ui/intersection-fade-in";
import { SidebarLayout } from "@/core/shared/v2/ui/sidebar-layout";
import { useGetPackages } from "../../api/get-packages";
import { CatalogList } from "../../components/v2/catalog-list";
import { SideCart } from "../../components/v2/side-cart";
import { useFlowPath } from "../../hooks/use-flow-path";
import type { BloodTestPackage } from "../../models/blood-test-package";
import { useCartState } from "../../store/use-cart-state";
import { useVouchersDistributionState } from "../../store/use-vouchers-distribution-state";
import { beginCheckoutEvent, safeTrackEvent } from "../../tracking/events";
import { configName } from "../config/config-name";
import { routes } from "../config/routes";

export const PickUpsell = () => {
  const history = useHistory();
  const flowPath = useFlowPath();
  const { t } = useTranslation();
  const { voucherDistribution } = useVouchersDistributionState();
  const { products, addProduct, removeProduct, clearCart } = useCartState();

  const getPackagesQuery = useGetPackages({
    slug: voucherDistribution?.slug || "",
    configName,
  });

  const upsellUuids = products.flatMap((p) => (p as unknown as BloodTestPackage).upsellBloodTestPackageUuids || []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: upsellUuids.join stabilizes array comparison
  const vouchersList = useMemo(
    () => getPackagesQuery.data?.bloodTestPackages?.filter((p) => upsellUuids.includes(p.uuid)),
    [getPackagesQuery.data?.bloodTestPackages, upsellUuids.join(",")],
  );

  if (getPackagesQuery.isLoading || !vouchersList)
    return (
      <div className="fixed inset-0 z-50 flex h-screen w-full items-center justify-center">
        <Loader />
      </div>
    );

  return (
    <IntersectionFadeIn className={cn(["mx-auto w-full max-w-[1376px] px-4 pt-8 pb-14 md:px-8 md:pb-20 lg:px-12"])}>
      <div className={cn("flex flex-col items-start max-lg:mb-8")}>
        <h1 className="font-serif text-2xl lg:text-3xl">{t("cmsTranslations.free_packages.upsell_catalog.title")}</h1>
        <p className="mt-1.5 text-base text-muted-foreground">
          {t("cmsTranslations.free_packages.upsell_catalog.description")}
        </p>
        <button
          type="button"
          className="mt-1 flex items-center border-b text-gray-600 text-sm"
          onClick={() => history.push(flowPath(routes.pickVoucher.raw()))}
        >
          <ArrowLeftIcon />
          {t("COMMONS.GENERAL.BACK")}
        </button>
      </div>
      <SidebarLayout>
        <CatalogList
          vouchers={vouchersList}
          allowMultiple={true}
          simplified={(vouchersList?.length || 0) > 7}
          selectedProductUuids={products.map((p) => p.uuid)}
          onAddProduct={addProduct}
          onRemoveProduct={removeProduct}
          onClearCart={clearCart}
          itemListId="upsell"
        />
        <SideCart
          ctaText={t("VOUCHERS_DISTRIBUTION.FREE_PACKAGES.CONTINUE")}
          onNextStep={() => {
            if (products.length > 0) {
              const analyticsProducts = products.map((p) => ({
                ...(p as unknown as BloodTestPackage),
                productType: "blood-test-package" as const,
                quantity: 1,
              }));
              const totalPrice = products.reduce((acc, p) => acc + Number(p.grossPrice), 0);
              safeTrackEvent(
                () =>
                  beginCheckoutEvent(analyticsProducts, { value: totalPrice }, { numberOfProducts: products.length }),
                "begin_checkout",
                "pick-upsell continue",
              );
            }
            history.push(flowPath(routes.checkout.raw()));
          }}
        />
      </SidebarLayout>
    </IntersectionFadeIn>
  );
};
